import {AuthTokenStorage} from "./AuthTokenStorage";
import {LocalStorage} from "./LocalStorage";
import {makeAutoObservable} from "mobx";
import {OrderStorage} from "./OrderStorage";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import {UserStorage} from "./UserStorage";
import {CustomerStorage} from "./CustomerStorage";
import {ChatHistoryStorage} from "./ChatHistoryStorage";

export default class Session {
    constructor(authenticationMethod) {
        this._authenticationMethod = authenticationMethod
        this._application = undefined;
        const localStorage = new LocalStorage();
        this._authTokenStorage = new AuthTokenStorage(localStorage);
        this._orderStorage = new OrderStorage(localStorage);
        this._userStorage = new UserStorage(localStorage);
        this._customerStorage = new CustomerStorage(localStorage)
        this._chatHistoryStorage = new ChatHistoryStorage(localStorage)
        this._active = false;
        this._loggingOut = false;
        makeAutoObservable(this);
    }

    withApplication(application) {
        this._application = application;
    }

    active() {
        this._active = !this._loggingOut && this._authenticationMethod.isUserLoggedIn() && this._userStorage.hasUser()
        return this._active
    }

    async userRolesOf(email) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                return response.roles()
            },
            handlesError: (response) => {
            }
        });
        return this._application.apiClient().userWithRoles(responseHandler, email);
    }

    async customersOf(email) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                return response.customers()
            },
            handlesError: (response) => {
            }
        });
        return this._application.apiClient().userWithRoles(responseHandler, email);
    }

    async successfulLoginOf(email) {
        const userRoles = await this.userRolesOf(email);
        const customers = await this.customersOf(email);
        const loginHasAssociatedCustomers = Array.isArray(customers) && customers.length > 0;
        if (!loginHasAssociatedCustomers) {
            return false;
        }
        this._customerStorage.addCustomers(customers);
        this._customerStorage.addCustomer(customers[0])
        this._userStorage.addUser({email: email, roles: userRoles});
        this._active = true;
        return true;
    }

    user() {
        return this._userStorage.getUser();
    }

    customers() {
        return this._customerStorage.getCustomers();
    }

    addToken(token) {
        this._authTokenStorage.addToken(token)
    }

    getToken() {
        return this._authTokenStorage.getToken()
    }

    addOrder(order) {
        return this._orderStorage.addOrder(order)
    }

    removeOrder() {
        return this._orderStorage.removeOrder()
    }

    getOrder() {
        return this._orderStorage.getOrder()
    }

    getCustomer() {
        return this._customerStorage.getCustomer()
    }

    chatSessionId() {
        return this._chatHistoryStorage.getOrCreateSessionId()
    }

    chatHistory() {
        return this._chatHistoryStorage.getMessages()
    }

    saveChatHistory(messages) {
        return this._chatHistoryStorage.addMessages(messages)
    }

    clearChatHistory() {
        return this._chatHistoryStorage.clear()
    }

    addCustomer(customer) {
        return this._customerStorage.addCustomer(customer)
    }

    hasCustomer() {
        return this._customerStorage.hasCustomer()
    }

    hasOrder() {
        return this._orderStorage.hasOrder()
    }

    async logOut() {
        this._loggingOut = true;
        await this._authenticationMethod.signOut().then((result) =>
            {
                this._authTokenStorage.removeToken()
                this._userStorage.removeUser()
                this._customerStorage.removeCustomers()
                this._chatHistoryStorage.clear()
                this._active = false
                this._loggingOut = false;
            }
        )
        return this.active();
    }

    async logOutAndRemoveOrder() {
        this._loggingOut = true;
        await this._authenticationMethod.signOut().then((result) =>
            {
                this._authTokenStorage.removeToken()
                this._userStorage.removeUser()
                this._customerStorage.removeCustomers()
                this._orderStorage.removeOrder()
                this._chatHistoryStorage.clear()
                this._active = false
                this._loggingOut = false;
            }
        )
        return this.active();
    }

    async refreshUserToken() {
        const token = await this._authenticationMethod.refreshUserToken()
        this.addToken(token)
    }
}
